import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AccountSettings from './pages/shared/AccountSettings';
import LoanOfficerWorkspace from './pages/lo/LoanOfficerWorkspace';
import MyResources from './pages/lo/MyResources';
import WorkflowBuilder from './pages/admin/WorkflowBuilder';
import Prospects from './pages/admin/Prospects';
import ProspectForm from './pages/admin/ProspectForm';
import RecruitForm from './pages/admin/RecruitForm';
import LoanOfficers from './pages/admin/LoanOfficers';
import LoanOfficerForm from './pages/admin/LoanOfficerForm';
import LoanOfficerDetail from './pages/admin/LoanOfficerDetail';
import NotificationSettings from './pages/admin/NotificationSettings';
import EmailTemplates from './pages/admin/EmailTemplates';
import RecruiterProspectForm from './pages/public/RecruiterProspectForm';
import OnboardingForm from './pages/public/OnboardingForm';
import HandsOnRoster from './pages/admin/HandsOnRoster';
import NotFound from './pages/shared/NotFound';
import ChangePasswordModal from './components/ChangePasswordModal';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const SplashScreen = ({ message }: { message: string }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb] text-slate-500">
    <div className="text-center space-y-4">
      <div className="h-12 w-12 rounded-full border-2 border-[#cfe5f0] border-t-lumin-primary animate-spin mx-auto" />
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  </div>
);

const ProtectedRoute: React.FC<{
  children: React.ReactElement;
  adminOnly?: boolean;
  staffOnly?: boolean; // For routes accessible by both admin and VA
}> = ({ children, adminOnly, staffOnly }) => {
  const { loanOfficer, loading, isAdmin, canAccessAdmin } = useAuth();

  if (loading) return <SplashScreen message="Spooling up the onboarding workspace..." />;

  if (!loanOfficer) {
    return <Navigate to="/login" replace />;
  }

  // Admin-only routes (e.g., Step Management)
  if (adminOnly && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb] text-slate-900">
        <div className="text-center space-y-4">
          <p className="text-sm uppercase tracking-[0.4em] text-lumin-primary">Admin only</p>
          <h1 className="text-3xl font-semibold">You found a restricted airlock.</h1>
          <p className="text-slate-500">Switch to an admin account to manage workflows.</p>
        </div>
      </div>
    );
  }

  // Staff routes (admin + VA)
  if (staffOnly && !canAccessAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb] text-slate-900">
        <div className="text-center space-y-4">
          <p className="text-sm uppercase tracking-[0.4em] text-lumin-primary">Staff only</p>
          <h1 className="text-3xl font-semibold">You found a restricted airlock.</h1>
          <p className="text-slate-500">This area is for staff members only.</p>
        </div>
      </div>
    );
  }

  return children;
};

const AppRoutes = () => {
  const { loanOfficer, loading, canAccessAdmin, mustChangePassword } = useAuth();
  const [passwordChanged, setPasswordChanged] = useState(false);

  if (loading) return <SplashScreen message="Loading your personalized experience..." />;

  // Show change password modal if required
  const showPasswordModal = loanOfficer && mustChangePassword && !passwordChanged;

  return (
    <>
      <ChangePasswordModal
        isOpen={showPasswordModal || false}
        onSuccess={() => setPasswordChanged(true)}
      />
      <Routes>
      {/* Login page - redirect to appropriate dashboard if already authenticated */}
      <Route
        path="/login"
        element={
          loanOfficer ? (
            <Navigate to={canAccessAdmin ? '/admin/prospects' : '/onboarding'} replace />
          ) : (
            <Login />
          )
        }
      />

      <Route
        path="/"
        element={
          loanOfficer ? (
            <Navigate to={canAccessAdmin ? '/admin/prospects' : '/onboarding'} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <LoanOfficerWorkspace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/onboarding/completed"
        element={
          <ProtectedRoute>
            <LoanOfficerWorkspace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/hands-on"
        element={
          <ProtectedRoute>
            <LoanOfficerWorkspace section="hands_on" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/onboarding/resources"
        element={
          <ProtectedRoute>
            <MyResources />
          </ProtectedRoute>
        }
      />

      <Route path="/admin" element={<Navigate to="/admin/prospects" replace />} />

      <Route
        path="/admin/workflows"
        element={
          <ProtectedRoute adminOnly>
            <WorkflowBuilder />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/hands-on"
        element={
          <ProtectedRoute staffOnly>
            <HandsOnRoster />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/loan-officers"
        element={
          <ProtectedRoute staffOnly>
            <LoanOfficers />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/loan-officers/new"
        element={
          <ProtectedRoute staffOnly>
            <LoanOfficerForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/loan-officers/:id"
        element={
          <ProtectedRoute staffOnly>
            <LoanOfficerDetail />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/prospects"
        element={
          <ProtectedRoute staffOnly>
            <Prospects />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/prospects/new"
        element={
          <ProtectedRoute staffOnly>
            <ProspectForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/recruits/new"
        element={
          <ProtectedRoute staffOnly>
            <RecruitForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/notification-settings"
        element={
          <ProtectedRoute adminOnly>
            <NotificationSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/email-templates"
        element={
          <ProtectedRoute adminOnly>
            <EmailTemplates />
          </ProtectedRoute>
        }
      />

      {/* Redirect old Timeline route to unified Recruiting page */}
      <Route
        path="/admin/timeline"
        element={<Navigate to="/admin/prospects" replace />}
      />

      {/* Auth pages - no auth required */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Account settings - protected */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        }
      />

      {/* Public recruiter form - no auth required */}
      <Route path="/recruit" element={<RecruiterProspectForm />} />

      {/* Public prospect onboarding form - no auth required */}
      <Route path="/onboarding-form" element={<OnboardingForm />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
